import {
  ArrowLeftOutlined,
  CopyOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Alert, Button, Layout, Steps, Typography } from "antd";
import axios from "axios";
import qs from "query-string";
import React, { useCallback, useEffect, useState } from "react";
import { UNKNOWN_ERROR_MESSAGE } from "../constants";
import { persistLinkState } from "../hooks/useLinkTracker";
import { captureException } from "../sentry";
import Store from "../stateStore";
import { extractLinkParamsFromState, getBackendUrl, STORES } from "../utils";

const { Step } = Steps;
const { Paragraph, Text } = Typography;

type Props = {
  clientSecret: string;
  onBack: () => any;
  onCancel: () => any;
  onComplete: (public_token: string) => any;
};

type SetupSession = {
  itemId: string;
  inboundAccessToken: string;
  installerUrl: string;
  // Populated only once POST /embedded_actions/dynamics365/setup-session
  // returns a public_token for this item. As of rutter-backend#17827 it does
  // not yet — see finishSetup below, which surfaces that gap rather than
  // guessing at a value.
  publicToken?: string;
};

const SETUP_STORAGE_KEY = "rutterDynamics365EmbeddedSetup";

type StoredSetup = {
  orgId: string;
  itemId: string;
  inboundAccessToken: string;
  installerUrl: string;
  publicToken?: string;
};

function Dynamics365Embedded(props: Props) {
  const store = Store.useStore();
  const autoPlatform = store.get("autoPlatform");
  const orgId = store.get("orgId");
  const existingItemId = store.get("itemId");
  // Resuming a prior visit reconstructs the full setup session from
  // sessionStorage instead of leaving `setup` null. Leaving it null would let
  // the effect below re-invoke startSetup() on every remount, hitting the
  // mint endpoint again for an item that's already set up — if it ever
  // reissues the inbound access token, that silently invalidates whatever
  // the user already pasted into Business Central. Only trust storage that
  // has the full session (older/partial entries fall back to a fresh mint).
  const [setup, setSetup] = useState<SetupSession | null>(() => {
    try {
      const stored = window.sessionStorage.getItem(SETUP_STORAGE_KEY);
      if (!stored) return null;
      const parsed = JSON.parse(stored) as StoredSetup;
      if (
        parsed.orgId !== orgId ||
        !parsed.itemId ||
        !parsed.inboundAccessToken ||
        !parsed.installerUrl
      ) {
        return null;
      }
      return {
        itemId: parsed.itemId,
        inboundAccessToken: parsed.inboundAccessToken,
        installerUrl: parsed.installerUrl,
        publicToken: parsed.publicToken,
      };
    } catch {
      return null;
    }
  });
  const [stepNumber, setStepNumber] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const activeItemId = setup?.itemId || existingItemId;

  const handleAuthError = useCallback((e: any) => {
    captureException(e);
    setError(
      e.response?.data?.reason ??
        e.response?.data?.error_message ??
        UNKNOWN_ERROR_MESSAGE,
    );
  }, []);

  const startSetup = useCallback(async () => {
    setLoading(true);
    setError("");
    persistLinkState(STORES.DYNAMICS365_EMBEDDED, store.getState());
    try {
      const { data } = await axios.post(
        getBackendUrl() + "/embedded_actions/dynamics365/setup-session",
        {
          ...extractLinkParamsFromState(store),
          link_update_id: activeItemId || undefined,
        },
      );
      if (!data.is_successful) {
        setError(data.reason ?? UNKNOWN_ERROR_MESSAGE);
        return;
      }
      setSetup({
        itemId: data.item_id,
        inboundAccessToken: data.inbound_access_token,
        installerUrl: data.installer_url,
        publicToken: data.public_token,
      });
      store.set("itemId")(data.item_id);
      window.sessionStorage.setItem(
        SETUP_STORAGE_KEY,
        JSON.stringify({
          orgId,
          itemId: data.item_id,
          inboundAccessToken: data.inbound_access_token,
          installerUrl: data.installer_url,
          publicToken: data.public_token,
        } as StoredSetup),
      );
    } catch (e) {
      handleAuthError(e);
    } finally {
      setLoading(false);
    }
  }, [activeItemId, handleAuthError, orgId, store]);

  useEffect(() => {
    if (!setup) {
      void startSetup();
    }
  }, [setup, startSetup]);

  const downloadApp = () => {
    if (!setup?.installerUrl) return;
    const link = document.createElement("a");
    link.href = setup.installerUrl;
    link.download = "RutterMarketTimeSample.app";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setStepNumber(1);
  };

  const copyInboundToken = async () => {
    if (!setup?.inboundAccessToken) return;
    try {
      await navigator.clipboard.writeText(setup.inboundAccessToken);
      setCopied(true);
      setStepNumber((current) => Math.max(current, 4));
    } catch (e) {
      captureException(e);
      setError(
        "Could not copy the inbound access token. Select it and copy manually.",
      );
    }
  };

  const stepStatus = (step: number): "finish" | "process" | "wait" => {
    if (step < stepNumber) return "finish";
    return step === stepNumber ? "process" : "wait";
  };

  const authorizeOAuth = () => {
    persistLinkState(STORES.DYNAMICS365_EMBEDDED, store.getState());
    window.location.replace(
      getBackendUrl() +
        "/dynamics/app-url?" +
        qs.stringify(extractLinkParamsFromState(store)),
    );
  };

  // No verify/poll endpoint exists for this flow (unlike Sage 50's
  // /sage-50/link-verify) — Business Central setup can't be confirmed
  // server-side, so completion is the user's own attestation once they reach
  // the final step. Reports back the public_token from the setup-session
  // response; if the backend never populated one, this surfaces that as an
  // error instead of calling onComplete with a fabricated value.
  const finishSetup = () => {
    if (!setup?.publicToken) {
      setError(
        "This session has no public token to report back yet — the setup-session response didn't include one. Contact Rutter support before closing this window.",
      );
      return;
    }
    window.sessionStorage.removeItem(SETUP_STORAGE_KEY);
    props.onComplete(setup.publicToken);
  };

  return (
    <Layout className="bg-white w-full">
      <Layout.Content className="overflow-y-auto px-16">
        <div className="w-full flex justify-center">
          <div className="w-full max-w-[800px] py-8">
            {!autoPlatform && (
              <Button onClick={props.onBack} icon={<ArrowLeftOutlined />}>
                Back
              </Button>
            )}
            <div className="text-center mb-8">
              <img
                className="h-12 inline-block"
                src="https://rutterpublicimages.s3.us-east-2.amazonaws.com/logos/dynamics-logo.png"
                alt="Dynamics 365 Business Central"
              />
            </div>
            <Paragraph className="text-center mb-6">
              Install MarketTime in Business Central, then paste the keys below
              on the MarketTime Setup page.
            </Paragraph>

            <Steps direction="vertical" current={stepNumber}>
              <Step
                title="Download the MarketTime app"
                status={stepStatus(0)}
                description={
                  <div className="space-y-3">
                    <Paragraph>
                      This is a per-tenant extension (.app), not an AppSource
                      listing. You will upload it in Business Central.
                    </Paragraph>
                    <Button
                      type="primary"
                      loading={loading}
                      disabled={!setup}
                      icon={<DownloadOutlined />}
                      onClick={downloadApp}
                    >
                      Download .app file
                    </Button>
                  </div>
                }
              />
              <Step
                title="Install it in Business Central"
                status={stepStatus(1)}
                description={
                  <div>
                    <Paragraph>In Business Central:</Paragraph>
                    <ol className="list-decimal ml-6 mb-3 space-y-1">
                      <li>
                        Tell Me → <Text strong>Extension Management</Text> →
                        Manage → Upload Extension → the downloaded .app file.
                        Accept the per-tenant disclaimer and deploy.
                      </li>
                      <li>
                        On the app tile, Configure → turn on{" "}
                        <Text strong>Allow HttpClient Requests</Text>. Without
                        this, live calls are blocked.
                      </li>
                      <li>
                        Users → assign permission set{" "}
                        <Text strong>Rutter MarketTime Sample</Text>.
                      </li>
                    </ol>
                    <Button onClick={() => setStepNumber(2)}>
                      I installed the app
                    </Button>
                  </div>
                }
              />
              <Step
                title="Open MarketTime Setup"
                status={stepStatus(2)}
                description={
                  <div>
                    <Paragraph>
                      Tell Me → <Text strong>MarketTime Setup</Text>. Leave
                      Rutter Base URL as{" "}
                      <Text code>https://production.rutterapi.com</Text> unless
                      you were given a different endpoint.
                    </Paragraph>
                    <Button onClick={() => setStepNumber(3)}>
                      I have Setup open
                    </Button>
                  </div>
                }
              />
              <Step
                title="Paste the Rutter inbound access token"
                status={stepStatus(3)}
                description={
                  <div className="space-y-3">
                    <Paragraph>
                      This is the only value Rutter generates. On Setup it is{" "}
                      <Text strong>Rutter Inbound Access Token</Text> →{" "}
                      <Text strong>Set Inbound Access Token</Text>.
                    </Paragraph>
                    {setup?.inboundAccessToken ? (
                      <>
                        <Paragraph
                          copyable={{ text: setup.inboundAccessToken }}
                          className="font-mono break-all bg-gray-50 p-2"
                        >
                          {setup.inboundAccessToken}
                        </Paragraph>
                        <Button
                          icon={<CopyOutlined />}
                          onClick={copyInboundToken}
                        >
                          {copied ? "Copied" : "Copy inbound access token"}
                        </Button>
                      </>
                    ) : (
                      <Text type="secondary">Preparing your installation…</Text>
                    )}
                  </div>
                }
              />
              <Step
                title="Paste Foundry and MarketTime API keys"
                status={stepStatus(4)}
                description={
                  <div>
                    <Paragraph>
                      MarketTime / Foundry give you these. They are not Rutter
                      credentials.
                    </Paragraph>
                    <ul className="list-disc ml-6 mb-3 space-y-1">
                      <li>
                        <Text strong>Foundry API Key</Text> — Foundry portal →
                        Settings → API Keys. Then{" "}
                        <Text strong>Set Foundry API Key</Text>.
                      </li>
                      <li>
                        <Text strong>MarketTime API Key</Text> — MarketTime
                        manufacturer portal (the same key the NetSuite mtCharge+
                        bundle asks for). Then{" "}
                        <Text strong>Set MarketTime API Key</Text>.
                      </li>
                    </ul>
                    <Paragraph>
                      Click <Text strong>Save Payment Keys to Rutter</Text>,
                      then <Text strong>Test Connection</Text>. When that
                      succeeds, turn <Text strong>Enabled</Text> on.
                    </Paragraph>
                    <Button onClick={() => setStepNumber(5)}>
                      I saved the keys
                    </Button>
                  </div>
                }
              />
              <Step
                title="Authorize Business Central access"
                status={stepStatus(5)}
                description={
                  <div className="space-y-3">
                    <Paragraph>
                      One more step lets Rutter independently verify records in
                      Business Central (used to confirm customer, payment, and
                      posting details server-side). Sign in with a Business
                      Central admin account and approve access.
                    </Paragraph>
                    <Button type="primary" onClick={authorizeOAuth}>
                      Authorize with Microsoft
                    </Button>
                    <div>
                      <Button type="link" onClick={() => setStepNumber(6)}>
                        Skip for now
                      </Button>
                    </div>
                  </div>
                }
              />
              <Step
                title="Finish remaining setup in Business Central"
                status={stepStatus(6)}
                description={
                  <div>
                    <Paragraph>
                      Stay on MarketTime Setup for accounting and email. Link
                      does not configure those.
                    </Paragraph>
                    <ul className="list-disc ml-6 mb-3 space-y-1">
                      <li>
                        Payout Posting — Suggest Accounts, then Check Payout
                        Posting Setup (clearing, bank, fees, chargebacks).
                      </li>
                      <li>
                        Email Accounts if you will send payment links, then
                        optionally assign the MarketTime Payments scenario.
                      </li>
                      <li>Optional: Enable Daily Payout Sync.</li>
                    </ul>
                    <Paragraph type="secondary">
                      Once Test Connection is green and Enabled is on, finish
                      below to complete this Link session.
                    </Paragraph>
                    <Button type="primary" onClick={finishSetup}>
                      I&apos;m done — finish setup
                    </Button>
                  </div>
                }
              />
            </Steps>

            <div className="mt-6">
              <Button onClick={props.onCancel}>Cancel</Button>
            </div>

            {error && (
              <Alert
                className="mt-4"
                type="error"
                message="Unable to start MarketTime setup"
                description={error}
                showIcon
              />
            )}
          </div>
        </div>
      </Layout.Content>
    </Layout>
  );
}

export default Dynamics365Embedded;
